import { AnyAction, ThunkAction, createAsyncThunk } from '@reduxjs/toolkit';

import axios from 'old-store/utils/axios';
import { setCollectionCreator, setDownloadPin, setNewAppModalShown } from 'store/slices/collection';
import { Api } from 'old-store/utils/API';
import { RootState } from 'old-store/store';
import { updateSiteTracking } from 'old-store/actions/environment';
import { getErrorStatus } from 'helpers/network';
import { preloadIconSet } from 'helpers/iconRegistry';
import { resolveIconSet } from 'helpers/iconSet';
import { HttpStatus } from 'constants/statusCodes';

// Resolves to whether the PIN was accepted; on success `setDownloadPin` also marks the collection
// as verified for this page, so the modal isn't asked for again until a reload.
export const checkDownloadPin = createAsyncThunk<
  boolean,
  { pin: string; collection_id: string },
  { state: RootState }
>('collection/checkDownloadPin', async ({ pin, collection_id }, { dispatch }) => {
  try {
    await Api.Collection.checkDownloadPin({ pin, collection_id });

    dispatch(setDownloadPin(pin));

    return true;
  } catch (error) {
    console.log(error);

    return false;
  }
});

export const markNewAppModalShown =
  (): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => async (dispatch) => {
    try {
      await Api.Site.markNewAppModalShown();
      dispatch(setNewAppModalShown());
    } catch (error) {
      console.log(error);
    }
  };

// `/user` without a query is 403'd while a gallery is locked; `?fields=featureFlags` still answers
// but the backend projects on it, so that response is only the flags the password page needs.
export const RESTRICTED_CREATOR_FIELDS = 'featureFlags';

const fetchCollectionCreator = async (fields?: string) => {
  const {
    data: { result }
  } = await axios.get(
    `/api/collections/${window.SITE_ID}/user`,
    fields ? { params: { fields } } : undefined
  );

  return result;
};

export const getCollectionCreator = createAsyncThunk(
  'collection/collectionCreator',
  async (fields: string | undefined, { dispatch, getState }: any) => {
    try {
      let result;

      if (fields) {
        result = await fetchCollectionCreator(fields);
      } else {
        // Ask for the full record; fall back to the restricted one only while the gallery is locked.
        try {
          result = await fetchCollectionCreator();
        } catch (e) {
          if (getErrorStatus(e) !== HttpStatus.Forbidden) throw e;

          result = await fetchCollectionCreator(RESTRICTED_CREATOR_FIELDS);
        }
      }

      dispatch(setCollectionCreator(result));

      // Earliest point where both the newDesign flag and design.iconSet are known, so the
      // set's chunk is normally resolved before any gallery chrome mounts and icons paint
      // in the chosen set rather than swapping from feather.
      preloadIconSet(
        resolveIconSet(
          (getState() as RootState).collection?.design,
          result?.featureFlags?.newDesign === true
        )
      );

      // A restricted response carries no domains — dispatching it would clear them.
      if (result?.builderDomain || result?.customDomain) {
        dispatch(
          updateSiteTracking({
            builderDomain: result.builderDomain || '',
            galleryDomain: result.customDomain || ''
          })
        );
      }

      return result;
    } catch (e) {
      console.log('e', e);
    }
  }
);
